import { useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Camera, X, Upload, ImagePlus, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { AnnouncementFormData } from '@/types/announcement';
import { EncouragementChibiGenerator } from '@/components/wizard/EncouragementChibiGenerator';
import { toast } from 'sonner';

const copy = {
  en: { title: 'Choose Your Photos', subtitle: 'Select 1-3 meaningful photos that capture your relationship. These will make your announcement more personal and memorable.', addPhoto: 'Add photo', optional: 'Optional', upload: 'Upload Photos', fileNote: 'JPG, PNG, WebP or GIF. HEIC/HEIF needs to be converted first.', tipsTitle: '💡 Tips for great photos:', tip1: 'Choose photos that show genuine moments together', tip2: 'A mix of recent and nostalgic photos works beautifully', tip3: "Photos don't need to be perfect—authentic is better!" },
  es: { title: 'Elige Tus Fotos', subtitle: 'Selecciona 1-3 fotos significativas que capturen tu relación. Harán tu anuncio más personal y memorable.', addPhoto: 'Agregar foto', optional: 'Opcional', upload: 'Subir Fotos', fileNote: 'JPG, PNG, WebP o GIF. Máx 5MB cada una.', tipsTitle: '💡 Consejos para buenas fotos:', tip1: 'Elige fotos que muestren momentos genuinos juntos', tip2: 'Una mezcla de fotos recientes y nostálgicas funciona bien', tip3: '¡Las fotos no necesitan ser perfectas—la autenticidad es mejor!' },
  fr: { title: 'Choisissez Vos Photos', subtitle: 'Sélectionnez 1 à 3 photos significatives qui capturent votre relation. Elles rendront votre annonce plus personnelle.', addPhoto: 'Ajouter une photo', optional: 'Optionnel', upload: 'Télécharger les Photos', fileNote: 'JPG, PNG, WebP ou GIF. Max 5 Mo chacun.', tipsTitle: '💡 Conseils pour de belles photos :', tip1: 'Choisissez des photos de moments authentiques ensemble', tip2: 'Un mélange de photos récentes et nostalgiques fonctionne bien', tip3: "Les photos n'ont pas besoin d'être parfaites — l'authenticité prime !" },
  pt: { title: 'Escolha Suas Fotos', subtitle: 'Selecione 1-3 fotos significativas que representem seu relacionamento. Elas tornarão seu anúncio mais pessoal e memorável.', addPhoto: 'Adicionar foto', optional: 'Opcional', upload: 'Enviar Fotos', fileNote: 'JPG, PNG, WebP ou GIF. Máx 5MB cada.', tipsTitle: '💡 Dicas para ótimas fotos:', tip1: 'Escolha fotos de momentos genuínos juntos', tip2: 'Uma mistura de fotos recentes e nostálgicas funciona bem', tip3: 'As fotos não precisam ser perfeitas — autenticidade é melhor!' },
  ar: { title: 'اختر صورك', subtitle: 'اختر 1-3 صور ذات معنى تعبر عن علاقتكم. ستجعل إعلانك أكثر شخصية.', addPhoto: 'إضافة صورة', optional: 'اختياري', upload: 'رفع الصور', fileNote: 'JPG, PNG, WebP أو GIF. بحد أقصى 5 ميغابايت.', tipsTitle: '💡 نصائح لصور رائعة:', tip1: 'اختر صوراً تُظهر لحظات حقيقية معاً', tip2: 'مزيج من الصور الحديثة والقديمة يعمل بشكل جميل', tip3: 'الصور لا تحتاج أن تكون مثالية — الأصالة أفضل!' },
  ja: { title: '写真を選ぶ', subtitle: '関係を表す1〜3枚の大切な写真を選んでください。アナウンスをより個人的で思い出深いものにします。', addPhoto: '写真を追加', optional: '任意', upload: '写真をアップロード', fileNote: 'JPG, PNG, WebP, GIF。各最大5MB。', tipsTitle: '💡 素敵な写真のコツ：', tip1: '一緒の本物の瞬間を写した写真を選びましょう', tip2: '最近の写真と懐かしい写真を混ぜると素敵です', tip3: '写真は完璧でなくていい — 自然体が一番！' },
  ko: { title: '사진을 선택하세요', subtitle: '관계를 담은 1-3장의 의미있는 사진을 선택하세요. 공지를 더 개인적이고 기억에 남게 만듭니다.', addPhoto: '사진 추가', optional: '선택사항', upload: '사진 업로드', fileNote: 'JPG, PNG, WebP 또는 GIF. 각 최대 5MB.', tipsTitle: '💡 좋은 사진 팁:', tip1: '함께한 진심 어린 순간의 사진을 선택하세요', tip2: '최근 사진과 추억 사진을 섞으면 좋습니다', tip3: '사진은 완벽할 필요 없어요 — 진정성이 더 중요합니다!' },
  id: { title: 'Pilih Foto Anda', subtitle: 'Pilih 1-3 foto bermakna yang menangkap hubungan Anda. Ini akan membuat pengumuman lebih personal dan berkesan.', addPhoto: 'Tambah foto', optional: 'Opsional', upload: 'Unggah Foto', fileNote: 'JPG, PNG, WebP atau GIF. Maks 5MB masing-masing.', tipsTitle: '💡 Tips untuk foto yang bagus:', tip1: 'Pilih foto yang menunjukkan momen tulus bersama', tip2: 'Campuran foto terbaru dan nostalgia akan terlihat indah', tip3: 'Foto tidak perlu sempurna — yang otentik lebih baik!' },

  hi: { title: 'अपनी फ़ोटो चुनें', subtitle: '1-3 सार्थक फ़ोटो चुनें जो आपके रिश्ते को दर्शाती हों। ये आपकी घोषणा को अधिक व्यक्तिगत और यादगार बनाएंगी।', addPhoto: 'फ़ोटो जोड़ें', optional: 'वैकल्पिक', upload: 'फ़ोटो अपलोड करें', fileNote: 'JPG, PNG, WebP या GIF। प्रत्येक अधिकतम 5MB।', tipsTitle: '💡 शानदार फ़ोटो के लिए सुझाव:', tip1: 'ऐसी फ़ोटो चुनें जो एक साथ वास्तविक पलों को दर्शाती हों', tip2: 'हाल की और पुरानी फ़ोटो का मिश्रण खूबसूरती से काम करता है', tip3: "Photos don't need to be perfect—authentic is better!" },
};

interface PhotoUploadStepProps {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
  existingPhotoUrls?: string[];
  onRemoveExistingPhoto?: (index: number) => void;
}

export function PhotoUploadStep({ 
  formData, 
  updateFormData, 
  existingPhotoUrls = [], 
  onRemoveExistingPhoto 
}: PhotoUploadStepProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const totalPhotos = existingPhotoUrls.length + formData.photoFiles.length;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isSupportedPhoto = (file: File) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (['heic', 'heif'].includes(ext) || ['image/heic', 'image/heif'].includes(file.type)) {
      toast.error('HEIC/HEIF photos are not supported yet. Please choose JPG, PNG, WebP, or GIF.');
      return false;
    }
    if (file.size <= 0) {
      toast.error('That photo appears to be empty. Please choose another image.');
      return false;
    }
    const supportedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const supportedExts = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
    if (!supportedTypes.includes(file.type) && !supportedExts.includes(ext)) {
      toast.error('Please choose a JPG, PNG, WebP, or GIF photo.');
      return false;
    }
    return true;
  };

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []).filter(isSupportedPhoto);
      if (files.length === 0) return;
      const remainingSlots = 3 - existingPhotoUrls.length;
      const newFiles = [...formData.photoFiles, ...files].slice(0, remainingSlots);
      const currentPreviewUrls = formData.photoUrls.slice(existingPhotoUrls.length);
      const newUrls = newFiles.map((file, index) => {
        if (formData.photoFiles[index] === file && currentPreviewUrls[index]) return currentPreviewUrls[index];
        try {
          return URL.createObjectURL(file);
        } catch (error) {
          console.error('[Wizard] Local photo preview creation failed', { name: file.name, type: file.type, size: file.size, error });
          toast.error('Could not preview that photo. Please choose another image.');
          return '';
        }
      }).filter(Boolean);
      updateFormData({ photoFiles: newFiles, photoUrls: [...existingPhotoUrls, ...newUrls] });
      e.target.value = '';
    },
    [formData.photoFiles, formData.photoUrls, updateFormData, existingPhotoUrls]
  );

  const removeNewPhoto = useCallback(
    (newIndex: number) => {
      const newFiles = formData.photoFiles.filter((_, i) => i !== newIndex);
      const urlIndex = existingPhotoUrls.length + newIndex;
      if (formData.photoUrls[urlIndex]?.startsWith('blob:')) URL.revokeObjectURL(formData.photoUrls[urlIndex]);
      const newUrls = formData.photoUrls.slice(existingPhotoUrls.length).filter((_, i) => i !== newIndex);
      updateFormData({ photoFiles: newFiles, photoUrls: [...existingPhotoUrls, ...newUrls] });
    },
    [formData, updateFormData, existingPhotoUrls]
  );

  const openPhotoPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <Camera className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{c.title}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">{c.subtitle}</p>
      </div>

      <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
        {[0, 1, 2].map((index) => {
          const isExisting = index < existingPhotoUrls.length;
          const hasPhoto = isExisting || (index - existingPhotoUrls.length) < formData.photoFiles.length;
          const photoUrl = formData.photoUrls[index] || null;

          return (
            <motion.div
              key={index}
              className={cn(
                'aspect-square rounded-xl border-2 border-dashed transition-all overflow-hidden relative',
                hasPhoto ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50'
              )}
              whileHover={{ scale: 1.02 }}
            >
              {hasPhoto && photoUrl ? (
                <>
                  <img src={photoUrl} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                  {formData.template === 'custom' && (
                    <div className="absolute bottom-0 left-0 right-0 flex gap-1 p-1.5 bg-gradient-to-t from-black/60 to-transparent">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const bgIndex = formData.customSettings.backgroundPhotoIndex === index ? null : index;
                          updateFormData({
                            customSettings: { ...formData.customSettings, backgroundPhotoIndex: bgIndex },
                          });
                        }}
                        className={cn(
                          'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all',
                          formData.customSettings.backgroundPhotoIndex === index
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-white/20 text-white hover:bg-white/30'
                        )}
                        title="Set as background"
                      >
                        <Image className="h-3 w-3" />
                        {formData.customSettings.backgroundPhotoIndex === index ? 'Background' : 'Set BG'}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      if (isExisting && onRemoveExistingPhoto) onRemoveExistingPhoto(index);
                      else removeNewPhoto(index - existingPhotoUrls.length);
                    }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <button type="button" onClick={openPhotoPicker} className="flex flex-col items-center justify-center h-full w-full cursor-pointer p-4">
                  <ImagePlus className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <span className="text-xs text-muted-foreground text-center">
                    {totalPhotos === 0 && index === 0 ? c.addPhoto : c.optional}
                  </span>
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif" multiple onChange={handleFileChange} className="hidden" />

      {totalPhotos < 3 && (
        <div className="text-center">
          <Button type="button" variant="outline" className="gap-2" onClick={openPhotoPicker}>
            <Upload className="h-4 w-4" />
            {c.upload}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">{c.fileNote}</p>
        </div>
      )}

      {/* Chibi generator for encouragement template */}
      {formData.template === 'encouragement' && totalPhotos > 0 && (
        <div className="max-w-xl mx-auto">
          <EncouragementChibiGenerator
            photoFile={formData.photoFiles[0] || null}
            existingPhotoUrl={existingPhotoUrls[0] || null}
            recipientName={formData.recipientName}
            onChibiGenerated={(url) => {
              // Store chibi URL in metadata (deep-merged by updateFormData)
              updateFormData({
                metadata: { chibiUrl: url },
              } as any);
            }}
            chibiUrl={(formData.metadata as any)?.chibiUrl}
          />
        </div>
      )}

      <div className="bg-muted/50 rounded-lg p-4 max-w-xl mx-auto">
        <h4 className="font-medium text-sm mb-2">{c.tipsTitle}</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• {c.tip1}</li>
          <li>• {c.tip2}</li>
          <li>• {c.tip3}</li>
        </ul>
      </div>
    </motion.div>
  );
}
